/* eslint-disable camelcase */
import _ from 'lodash';
import qs from 'query-string';
import { getSnapshot } from 'mobx-state-tree';
import { ExportFormat } from '../../constants/file';
import { decamelizeKeys } from '../../lib';
import api, { rawClient } from '../api';
import {
    ContractRentalBaseInput,
    NewContractRentalInstance,
    NewContractRentalSnapshot,
    NewRentalParametersSnapshot,
    NewRentalTenantSnapshot,
    SplitParametersCharges,
    SplitParametersSnapshot,
    ContractRentalPaginatedResponse,
    ContractRentalFilterCountsSnapshot,
    RawRentalChargeRequestSnapshot,
    SplitChargeCreationOutput,
    SplitDestinationParametersCharges,
    ContractRentalCountsSummarySnapshot,
} from '../../models/RentalPayment';
import { PaginatedRequest } from '../../lib/pagination';
import { getBackVersion } from '../../config';
import {
    BillingStatuses,
    ContractStatus,
    RentalContractGuaranteeType,
} from '../../constants/rental';
import { DateTime } from 'luxon';
import { DimobAvailableYearsSnapshot } from '../../models/RentalPayment/Dimob';

type RawContractRentalRequest = Omit<
    NewContractRentalSnapshot,
    'charges' | 'tenants' | 'landlord' | 'splitCharges'
> & {
    charges: RawRentalChargeRequestSnapshot[];
    splitCharges: SplitChargeCreationOutput[];
    landlordId: number | undefined;
    tenants: NewRentalTenantSnapshot[];
} & Pick<
        NewRentalParametersSnapshot,
        'phoneNotificationsEnabled' | 'notificationsEnabled'
    >;

type NewContractRentalRequest = Omit<
    RawContractRentalRequest,
    'references' | 'stepArgs' | 'stepStack'
> &
    Partial<
        Pick<RawContractRentalRequest, 'references' | 'stepArgs' | 'stepStack'>
    >;

const makeNewContractRequest = (
    contract: NewContractRentalInstance
): NewContractRentalRequest => {
    const snapshot = getSnapshot(contract);
    return {
        ...snapshot,
        landlordId: contract.landlord?.id?.id,
        tenants: contract.tenantSnapshots,
        phoneNotificationsEnabled:
            contract.parameters.phoneNotificationsEnabled,
        notificationsEnabled: contract.parameters.notificationsEnabled,
        splitParameters:
            snapshot.splitParameters?.notificationsEnabled === undefined
                ? undefined
                : snapshot.splitParameters,
        splitCharges: snapshot.splitCharges.map(charge => ({
            ...charge,
            referenceMonths: charge.installments?.map(installment => ({
                referenceMonth: installment.referenceMonth,
                isSettled: installment.status === 'settled',
            })),
        })),
        charges: snapshot.charges.map(charge => ({
            ...charge,
            installments: charge.installments?.map(installment => ({
                referenceMonth: installment.referenceMonth,
                isSettled: installment.status === 'settled',
            })),
        })),
        references: undefined,
        stepArgs: undefined,
        stepStack: undefined,
    };
};

export type RawContractRentalResponse = Omit<
    ContractRentalBaseInput,
    'charges'
>;

const toSnapshot = ({
    ...response
}: RawContractRentalResponse): ContractRentalBaseInput => {
    return {
        ...response,
    };
};

const mapToSnapshot = (response: RawContractRentalResponse[]) =>
    response.map(toSnapshot);

const getRentalContract = (
    contractId: ContractRentalBaseInput['id']
): Promise<ContractRentalBaseInput> =>
    api
        .request<RawContractRentalResponse>({
            method: 'GET',
            url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}`,
        })
        .then(toSnapshot);

const getRentalContracts = (): Promise<ContractRentalBaseInput[]> =>
    api
        .request<ContractRentalPaginatedResponse>({
            method: 'GET',
            url: `${getBackVersion()}/agent/rental/contracts_rental`,
        })
        .then(response => mapToSnapshot(response.contracts));

type ContractRequestFilter = {
    landlord?: string;
    address?: string;
    tenant?: string;
    status?: ContractStatus[];
    isArchived?: boolean;
    minDate?: string;
    maxDAte?: string;
};

type ContractBillingsRequestFilter = {
    status?: BillingStatuses[];
    isArchived?: boolean;
    orderBy: string;
};
const getPaginatedContracts = (
    params: PaginatedRequest & ContractRequestFilter
): Promise<ContractRentalPaginatedResponse> => {
    return api.request<ContractRentalPaginatedResponse>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental`,
        params: decamelizeKeys({ ...params, paginate: true }),
        paramsSerializer: params => {
            // {a: [2, 3]} -> ?a=2&a=3
            // which is the supported format by the backend
            return qs.stringify(params, { arrayFormat: 'none' });
        },
    });
};
const getRentalContractsCounts =
    (): Promise<ContractRentalCountsSummarySnapshot> =>
        api.request<ContractRentalCountsSummarySnapshot>({
            method: 'GET',
            url: `${getBackVersion()}/agent/rental/contracts_rental/total/count`,
        });

const createContract = (
    contract: NewContractRentalInstance
): Promise<ContractRentalBaseInput> =>
    api
        .request<RawContractRentalResponse>({
            action: 'RENTAL_CONTRACT_CREATION',
            method: 'POST',
            url: `${getBackVersion()}/agent/rental/contracts_rental`,
            data: decamelizeKeys(makeNewContractRequest(contract)),
        })
        .then(toSnapshot);

const editParameters = (
    contractId: string,
    contract: NewContractRentalInstance
): Promise<ContractRentalBaseInput> => {
    const { parameters, beginDate, endDate, billingsToOverride } =
        getSnapshot(contract);

    return api
        .request<RawContractRentalResponse>({
            method: 'PATCH',
            url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/parameters`,
            data: decamelizeKeys({
                ..._.omit(parameters, ['charges']),
                observations: parameters.observations,
                rentCharge: contract.rent
                    ? getSnapshot(contract.rent)
                    : undefined,
                beginDate,
                endDate,
                notificationsEnabled: parameters.notificationsEnabled,
                phoneNotificationsEnabled: parameters.phoneNotificationsEnabled,
                billingsToOverride,
            }),
        })
        .then(toSnapshot);
};

const changeLandlord = (
    contractId: string,
    landlordId: number | null
): Promise<ContractRentalBaseInput> =>
    api
        .request<RawContractRentalResponse>({
            method: 'PATCH',
            action: 'RENTAL_CONTRACT_PATCH',
            url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}`,
            data: { landlord_id: landlordId },
        })
        .then(toSnapshot);

const upsertSplitParameters = (
    contractId: string,
    splitParameters: SplitParametersSnapshot,
    charges: SplitParametersCharges
): Promise<SplitParametersSnapshot> =>
    api.request<SplitParametersSnapshot>({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/split_parameters`,
        data: decamelizeKeys({ ...splitParameters, charges }),
    });

const upsertSplitChargeConfigurations = (
    contractId: string,
    charges: SplitDestinationParametersCharges | SplitParametersCharges,
    type: 'billing' | 'split'
): Promise<void> =>
    api.request<void>({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/split_configuration`,
        data: decamelizeKeys({ charges, type }),
    });

const downloadContractRentalReports = (
    extension: ExportFormat,
    filters: ContractRequestFilter
): Promise<void> => {
    return rawClient.download({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/download/${extension}`,
        params: decamelizeKeys(filters),
        paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'none' });
        },
    });
};

const requestContractRentalReports = (
    extension: ExportFormat,
    filters: ContractRequestFilter
): Promise<void> => {
    return api.request({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${extension}`,
        params: decamelizeKeys(filters),
        paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'none' });
        },
    });
};
const getContractRentalFilterCounts =
    (): Promise<ContractRentalFilterCountsSnapshot> => {
        return api.request({
            method: 'GET',
            url: `${getBackVersion()}/agent/rental/contracts_rental/total/filter_counts`,
        });
    };

const getDimobAvailableYears = (): Promise<DimobAvailableYearsSnapshot> =>
    api.request({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/dimob/years`,
    });

const requestContractsDimob = (
    format: ExportFormat,
    year: number
): Promise<void> =>
    api.request({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/dimob/${year}/${format}`,
    });

const downloadContractBillingsReport = (
    contractId: string,
    extension: ExportFormat,
    filters: ContractBillingsRequestFilter
): Promise<void> => {
    return rawClient.download({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${extension}`,
        params: decamelizeKeys(filters),
        paramsSerializer: params => {
            // {a: [2, 3]} -> ?a=2&a=3
            // which is the supported format by the backend
            return qs.stringify(params, { arrayFormat: 'none' });
        },
    });
};

const removeContract = (contractId: string): Promise<void> =>
    api.request({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}`,
        params: { action: 'remove' },
    });

const archiveContract = (
    contractId: string,
    action: 'archive' | 'unarchive'
): Promise<ContractRentalBaseInput> =>
    api
        .request<RawContractRentalResponse>({
            method: 'DELETE',
            url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}`,
            params: { action: action },
        })
        .then(toSnapshot);

type ContractExtraInfoParams = {
    contractId: string;
    contractNumber: string | null;
    observations: string | null;
};

const updateContractExtraInfo = ({
    contractId,
    contractNumber,
    observations,
}: ContractExtraInfoParams): Promise<ContractRentalBaseInput> =>
    api
        .request<RawContractRentalResponse>({
            method: 'PATCH',
            url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/extra_info`,
            params: {
                contract_number: contractNumber,
                observations,
            },
        })
        .then(toSnapshot);

const updateResidentialInsuranceDueDate = (
    contractId: string,
    residentialInsuranceDueDate: DateTime | null
): Promise<ContractRentalBaseInput> =>
    api
        .request<RawContractRentalResponse>({
            method: 'PATCH',
            url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/residential_insurance_due_date`,
            params: {
                residential_insurance_due_date:
                    residentialInsuranceDueDate?.toISODate(),
            },
        })
        .then(toSnapshot);

const upsertInsuranceInfo = (
    contractId: string,
    insuranceType: RentalContractGuaranteeType | null,
    insuranceValue: number | null,
    insuranceRenewalDate: DateTime | null
): Promise<ContractRentalBaseInput> =>
    api
        .request<RawContractRentalResponse>({
            method: 'PATCH',
            url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/guarantee_info`,
            params: {
                guarantee_type: insuranceType,
                guarantee_value: insuranceValue,
                renewal_date: insuranceRenewalDate?.toISODate(),
            },
        })
        .then(toSnapshot);

const updatePropertyInformation = (
    contractId: string,
    propertyObservation?: string,
    propertyCode?: string
): Promise<ContractRentalBaseInput> =>
    api
        .request<RawContractRentalResponse>({
            method: 'PATCH',
            url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/property`,
            params: decamelizeKeys({
                propertyCode,
                propertyObservation,
            }),
        })
        .then(toSnapshot);

export {
    createContract,
    changeLandlord,
    upsertSplitParameters,
    getDimobAvailableYears,
    getRentalContract,
    getRentalContracts,
    editParameters,
    requestContractsDimob,
    requestContractRentalReports,
    downloadContractBillingsReport,
    removeContract,
    archiveContract,
    getRentalContractsCounts,
    updateContractExtraInfo,
    updateResidentialInsuranceDueDate,
    getPaginatedContracts,
    getContractRentalFilterCounts,
    downloadContractRentalReports,
    upsertSplitChargeConfigurations,
    updatePropertyInformation,
    upsertInsuranceInfo,
};
